import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES } from '@/lib/constants/roles';
import { ensureArray } from '@/lib/utils/ensure-array';
import type {
  AdminResetPwdRequest,
  BlockUnblockRequest,
  CreditsChangeRequest,
  CreditsHistoryRequest,
  CreditsHistoryRow,
  SubSuperAdminRow,
  SwitchUserResponse,
  UserListFilterRequest,
  UserListResponse,
  UserProfile,
  UserSettingRow,
  UserSettingUpdateRequest,
} from './types';

/* --------------------------------------------------------------- *
 * Query-key helpers                                                *
 * --------------------------------------------------------------- */

export const userKeys = {
  all: ['users'] as const,
  list: (userId: string | number | null, filter?: UserListFilterRequest) =>
    filter ? (['users', 'list', userId, filter] as const) : (['users', 'list', userId] as const),
  allUsers: ['users', 'all'] as const,
  profile: (userId: string | number | null) => ['users', 'profile', userId] as const,
  subSuperAdmins: (userId: string | number | null) =>
    ['users', 'sub-super-admins', userId] as const,
  creditsHistory: (userId: string | number | null, range: { startDate: string; endDate: string }) =>
    ['users', 'credits-history', userId, range] as const,
  password: (userId: string | number | null) => ['users', 'password', userId] as const,
  settingsList: (userId: string | number | null) =>
    ['users', 'settings-list', userId] as const,
  settingDetail: (subId: string | number, userId: string | number) =>
    ['users', 'setting-detail', subId, userId] as const,
};

/** Minimal user row used by bulk-membership pickers. */
export interface AllUserRow {
  userId: number;
  username: string;
}

/** GET /api/obd/all/user/list — flat list of every user, used by bulk pages. */
export function useAllUsers() {
  return useQuery({
    queryKey: userKeys.allUsers,
    queryFn: () => api.get<AllUserRow[]>('/api/obd/all/user/list'),
    staleTime: 60_000,
    select: (data) => ensureArray<AllUserRow>(data),
  });
}

/* --------------------------------------------------------------- *
 * Main user list                                                   *
 *                                                                  *
 *   Roles:                                                         *
 *     - ADMIN  → GET  /api/obd/user/{userId}                        *
 *     - USER   → GET  /api/obd/user/{userId}   (sub-users)         *
 *     - SADMIN → POST /api/obd/filter/user/list  (filtered)        *
 *     - SUB_SADMIN → POST /api/obd/filter/user/list  (filtered)    *
 *                                                                  *
 * --------------------------------------------------------------- */

export interface UseUsersOpts {
  /** Filter only applies to SADMIN / SUB_SADMIN (filter endpoint). */
  filter?: { filterBy: UserListFilterRequest['filterBy']; filterData: string };
}

export function useUsers(opts: UseUsersOpts = {}) {
  const userId = useAuthStore((s) => s.userId);
  const isSubOrSuper = useAuthStore((s) => s.isSubOrSuperAdmin());

  const filterPayload =
    isSubOrSuper && opts.filter
      ? {
          filterBy: opts.filter.filterBy,
          filterData: opts.filter.filterData,
        }
      : undefined;

  return useQuery({
    queryKey: userKeys.list(userId, filterPayload ? { userId: Number(userId), ...filterPayload } : undefined),
    queryFn: async () => {
      if (!userId) throw new Error('Not authenticated');
      if (isSubOrSuper) {
        return api.post<UserListResponse>('/api/obd/filter/user/list', {
          userId: Number(userId),
          filterBy: filterPayload?.filterBy ?? '',
          filterData: filterPayload?.filterData ?? '',
        });
      }
      return api.get<UserListResponse>(`/api/obd/user/${userId}`);
    },
    enabled: Boolean(userId),
    staleTime: 30_000,
  });
}

/* --------------------------------------------------------------- *
 * Sub-super-admin list (SADMIN only)                               *
 *   GET /api/obd/sub/superadmin/user/list/{userId}                 *
 * --------------------------------------------------------------- */

export function useSubSuperAdmins() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: userKeys.subSuperAdmins(userId),
    queryFn: () => api.get<SubSuperAdminRow[]>(`/api/obd/sub/superadmin/user/list/${userId}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
  });
}

/* --------------------------------------------------------------- *
 * Self profile / arbitrary-user profile                            *
 *   GET /api/obd/user/profile/{userId}                             *
 * --------------------------------------------------------------- */

export function useUserDetails(targetUserId: string | number | null) {
  return useQuery({
    queryKey: userKeys.profile(targetUserId),
    queryFn: () => api.get<UserProfile>(`/api/obd/user/profile/${targetUserId}`),
    enabled: Boolean(targetUserId),
    staleTime: 60_000,
  });
}

/* --------------------------------------------------------------- *
 * Credits history                                                  *
 *   POST /api/obd/credits/history       (admin-side, sees audit)   *
 *   POST /api/obd/credits/history/user  (end-user, own log)        *
 * --------------------------------------------------------------- */

export function useCreditsHistory(range: { startDate: string; endDate: string }) {
  const userId = useAuthStore((s) => s.userId);
  const role = useAuthStore((s) => s.role);
  // Legacy routing: only ROLE_USER hits the per-user endpoint. ROLE_ADMIN
  // (reseller), SADMIN, and sub-super-admin all hit the audit endpoint —
  // they see entries for the users they manage.
  const isEndUser = role === USER_ROLES.USER;
  return useQuery({
    queryKey: userKeys.creditsHistory(userId, range),
    queryFn: () => {
      const url = isEndUser ? '/api/obd/credits/history/user' : '/api/obd/credits/history';
      const body: CreditsHistoryRequest = {
        userId: Number(userId),
        startDate: range.startDate,
        endDate: range.endDate,
      };
      return api.post<CreditsHistoryRow[]>(url, body);
    },
    enabled: Boolean(userId),
    staleTime: 30_000,
  });
}

/* --------------------------------------------------------------- *
 * Mutations                                                        *
 * --------------------------------------------------------------- */

function useInvalidateUsers() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: userKeys.all });
}

/** POST /api/obd/credits/add — add voice credits to a user. */
export function useAddCredits() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (req: CreditsChangeRequest) =>
      api.post<{ message?: string }>('/api/obd/credits/add', req),
    onSuccess: () => invalidate(),
  });
}

/** POST /api/obd/credits/remove — deduct voice credits. */
export function useRemoveCredits() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (req: CreditsChangeRequest) =>
      api.post<{ message?: string }>('/api/obd/credits/remove', req),
    onSuccess: () => invalidate(),
  });
}

/** POST /api/obd/tts/credits/add — add TTS credits. */
export function useAddTtsCredits() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (req: CreditsChangeRequest) =>
      api.post<{ message?: string }>('/api/obd/tts/credits/add', req),
    onSuccess: () => invalidate(),
  });
}

/** POST /api/obd/tts/credits/remove — deduct TTS credits. */
export function useRemoveTtsCredits() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (req: CreditsChangeRequest) =>
      api.post<{ message?: string }>('/api/obd/tts/credits/remove', req),
    onSuccess: () => invalidate(),
  });
}

/** POST /api/obd/pwd/reset — admin resets another user's password. */
export function useAdminResetPassword() {
  return useMutation({
    mutationFn: (req: AdminResetPwdRequest) =>
      api.post<{ message?: string }>('/api/obd/pwd/reset', req),
  });
}

/** POST /api/obd/block/unblock/user. */
export function useBlockUnblockUser() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (req: BlockUnblockRequest) =>
      api.post<{ message?: string }>('/api/obd/block/unblock/user', req),
    onSuccess: () => invalidate(),
  });
}

/** DELETE-equivalent — legacy uses POST /api/obd/user/{userId} with method override. */
export function useDeleteUser() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (targetUserId: number) => api.delete<{ message?: string }>(`/api/obd/user/${targetUserId}`),
    onSuccess: () => invalidate(),
  });
}

/** POST /api/obd/switch/user — impersonate. */
export function useSwitchUser() {
  return useMutation({
    mutationFn: (targetUserId: number) =>
      api.post<SwitchUserResponse>('/api/obd/switch/user', { userId: targetUserId }),
  });
}

/** GET /api/obd/pwd/{userId} — admin "show password" action. */
export function useFetchUserPassword() {
  return useMutation({
    mutationFn: (targetUserId: number) =>
      api.get<{ password?: string; message?: string }>(`/api/obd/pwd/${targetUserId}`),
  });
}

/** POST /api/obd/change/accountType. */
export interface ChangeAccountTypeRequest {
  userId: number;
  parent: number;
  accountType: 0 | 1;
}
export function useChangeAccountType() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (req: ChangeAccountTypeRequest) =>
      api.post<{ message?: string }>('/api/obd/change/accountType', req),
    onSuccess: () => invalidate(),
  });
}

/* --------------------------------------------------------------- *
 * Register (create) — single legacy endpoint serving every user    *
 * type. The caller assembles the right payload shape for its       *
 * variant (sub-super-admin, sub-user, full user).                  *
 *                                                                  *
 *   POST /api/obd/register   200 -> { message }                    *
 * --------------------------------------------------------------- */

/**
 * Payload for /api/obd/register. Marked as a loose record because the
 * legacy backend accepts many optional fields that only some variants
 * send. The form layer is responsible for sending the correct subset.
 */
export type RegisterPayload = Record<string, unknown> & {
  username: string;
  password: string;
  name: string;
  emailid: string;
  number: string | number;
  parent: number;
  accountType: 0 | 1;
  userType: string;
  expiryDate: string;
};

export function useRegisterUser() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (req: RegisterPayload) =>
      api.post<{ message?: string }>('/api/obd/register', req),
    onSuccess: () => invalidate(),
  });
}

/* --------------------------------------------------------------- *
 * Update — POST /api/obd/user/update. Same body shape as register *
 * minus password (passwords are reset via /api/obd/pwd/reset).    *
 * --------------------------------------------------------------- */

export type UserUpdatePayload = Record<string, unknown> & {
  userId: number;
  parent: number;
  accountType: 0 | 1;
  userType: string;
  expiryDate: string;
};

export function useUpdateUser() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (req: UserUpdatePayload) =>
      api.post<{ message?: string }>('/api/obd/user/update', req),
    onSuccess: () => invalidate(),
  });
}

/* --------------------------------------------------------------- *
 * User Settings (tier pricing).                                    *
 *                                                                  *
 *   GET  /api/obd/user/setting/list/{userId}                       *
 *   GET  /api/obd/user/setting/{subId}/{userId}                    *
 *   POST /api/obd/update/user/setting                              *
 * --------------------------------------------------------------- */

export function useUserSettingsList() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: userKeys.settingsList(userId),
    queryFn: () => api.get<UserSettingRow[]>(`/api/obd/user/setting/list/${userId}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (data) => ensureArray<UserSettingRow>(data),
  });
}

export function useUserSettingDetail(subId: string | number, targetUserId: string | number) {
  return useQuery({
    queryKey: userKeys.settingDetail(subId, targetUserId),
    queryFn: () =>
      api.get<UserSettingRow>(`/api/obd/user/setting/${subId}/${targetUserId}`),
    enabled: Boolean(targetUserId),
    staleTime: 60_000,
  });
}

export function useUpdateUserSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: UserSettingUpdateRequest) =>
      api.post<{ message?: string }>('/api/obd/update/user/setting', req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users', 'settings-list'] }),
  });
}
